import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { Order, OrderTransaction, PagedResponse, Team } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { ISelectedPaymentOptions } from '@reg/event/registration/payment/payment-item.model';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and save order data.
 */
@Injectable()
export class OrderService {
    public static INCLUDE_ORDER_ITEMS: string = 'orderItems';
    public static INCLUDE_ORDER_TRANSACTIONS: string = 'orderTransactions';
    public static INCLUDE_USER_ACCOUNT: string = 'userAccount';
    public static INCLUDE_TEAM: string = 'team';
    public static ORDER_TRANSACTION_STATUS_SUCCEEDED: string = 'succeeded';

    public floProStripeTokenId: string;

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all orders for the specified event
     */
    public getIndexListResponse(
        eventId: string,
        includes: string[] = [],
        queryParams: string[] = [],
        displaySpinner: boolean = true,
    ): Observable<PagedResponse> {
        queryParams.push(`eventId=${eventId}`);
        const apiRequestUrl: string = this.getApiUrl(null, includes, queryParams);
        return this.getIndexListResponseByUrl(eventId, apiRequestUrl, displaySpinner);
    }

    /**
     * Get orders by a url. This is used for paging through orders since the response
     * provides the proper page urls
     */
    public getIndexListResponseByUrl(
        eventId: string,
        url: string,
        displaySpinner: boolean = true,
    ): Observable<PagedResponse> {
        return this.http.get(url, displaySpinner)
            .pipe(map((response: IJsonApiResponse) => {
                const json: any = response;
                const orders: Order[] = json.data.map((d: any) => (new Order()).deserialize(d, json.included));

                return new PagedResponse(
                    json['links']['first'],
                    json['links']['last'],
                    json['links']['prev'],
                    json['links']['next'],
                    json['meta']['total'],
                    orders,
                );
            }));
    }

    /**
     * Get all orders for the specified event
     */
    public getIndex(
        eventId: string,
        userId?: number,
        teamId?: string,
        includes: string[] = null,
        queryParams: string[] = [],
        displaySpinner: boolean = true,
    ): Observable<Order[]> {
        queryParams.push(`eventId=${eventId}`);

        if (userId) {
            queryParams.push(`userId=${userId}`);
        }

        if (teamId) {
            queryParams.push(`teamId=${teamId}`);
        }

        const apiRequestUrl: string = this.getApiUrl(null, includes, queryParams);

        return this.http.get(apiRequestUrl, displaySpinner)
            .pipe(map((response: IJsonApiResponse) => {
                const orders: Order[] = [];
                const bodyData: any = response.data || [];
                for (const order of bodyData) {
                    orders.push((new Order()).deserialize(order, response.included));
                }
                return orders;
            }));
    }

    /**
     * Get a single order's data
     */
    public get(orderId: string, includes: string[] = null, queryParams: string[] = []): Observable<Order> {
        const apiRequestUrl: string = this.getApiUrl(orderId, includes, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new Order().deserialize(response, response.included))));
    }

    /**
     * Check if the current user has access to refund this order
     */
    public getRefundAccess(orderId: string): Observable<boolean> {
        const apiRequestUrl: string = `${this.getApiUrl(orderId)}/user-refund-access`;

        return this.http.get<IRefundAccess>(apiRequestUrl)
            .pipe(map((response: IRefundAccess) => response.hasAccess));
    }

    /**
     * Add a new order order
     */
    public add(eventId: string, teamId: string): Observable<Order> {
        const apiRequestUrl: string = this.getApiUrl(null, null);

        const order: Order = new Order();
        order.eventId = eventId;
        order.team = new Team(eventId, teamId, false);

        // Add a new Order
        return this.http.post(apiRequestUrl, order.serialize())
            .pipe(map((response: IJsonApiResponse) => (new Order()).deserialize(response, response.included)));
    }

    /**
     * apply a coupon to the order
     */
    public applyCoupon(
        order: Order,
        couponCode: string,
        includes: string[] = null,
        selectedPaymentOptions: ISelectedPaymentOptions = null,
    ): Observable<Order> {
        let apiRequestUrl: string = `${this.getApiUrl(order.id, null)}/apply-coupon`;
        apiRequestUrl += this.http.getQueryParameters(includes);

        return this.http.patch(apiRequestUrl, order.serializeForCoupon(couponCode, selectedPaymentOptions))
            .pipe(map((response: IJsonApiResponse) => (new Order()).deserialize(response, response.included)));
    }

    /**
     * Submit payment for the order
     */
    public submitPayment(orderId: string, chargeRequst: string, includes: string[] = null): Observable<Order> {
        let apiRequestUrl: string = `${this.getApiUrl(orderId, [], [])}/submit-payment`;
        apiRequestUrl += this.http.getQueryParameters(includes, null);

        return this.http.post(apiRequestUrl, JSON.stringify(chargeRequst))
            .pipe(map((response: IJsonApiResponse) => (new Order()).deserialize(response, response.included)));
    }

    /**
     * Build the order items and validate each item if it's a wrestler
     */
    public buildAndValidate(
        orderId: string,
        includes: string[] = null,
        selectedPaymentOptions: ISelectedPaymentOptions = null,
    ): Observable<Order> {
        let apiRequestUrl: string = `${this.getApiUrl(orderId, [], [])}/build-and-validate`;
        apiRequestUrl += this.http.getQueryParameters(includes, null);
        const body: any = { selectedPaymentOptions: selectedPaymentOptions };

        return this.http.patch(apiRequestUrl, JSON.stringify(body))
            .pipe(map((response: IJsonApiResponse) => (new Order()).deserialize(response, response.included)));
    }

    /**
     * If the order was created before we started to store transaction fees we need to back fill them.
     */
    public backFillTransactionFees(orderId: string): Observable<OrderTransaction[]> {
        const apiRequestUrl: string = `${this.getApiUrl(orderId)}/order-transactions/fees/`;

        return this.http.patch(apiRequestUrl, null)
            .pipe(
                map((response: IJsonApiResponse) => {
                    const bodyData: any = response.data || [];
                    return bodyData.map((data: any) => (new OrderTransaction()).deserialize(data));
                }),
            );
    }

    /**
     * Delete the specified order.
     */
    public delete(eventId: string, orderId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(orderId, null);
        return this.http.delete(apiRequestUrl).pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(orderId: string, includes?: string[], queryParams?: string[]): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/orders/`;

        if (orderId) {
            url += orderId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}

export interface IRefundAccess {
    hasAccess: boolean
}
